import { styled } from '@mui/material/styles';
import { Icon } from '../../../atoms';
import { NavLink } from '../Link';
import { NavDropdown } from '../Dropdown';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch, useIsMultiPortfolioTenant } from '../../../../common/hooks';
import { useEffect, useRef } from 'react';
import { getUserRelatedInfo, updateUserInfo } from '../../../../features/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import { ADVISOR_ROUTES } from '../../../../routes/routeConstants';
import { advisorConsoleActions, getAdvisorConsoleProposals } from '../../../../features/advisorConsole';
import { checkFFEnabled, getClientJourneyFlowType } from '../../../../utils';
import { FEATURE_NAMES } from '../../../../common/constants';

const Container = styled('span')<{ isActive: boolean }>(({ isActive = false }) => ({
    display: 'flex',
    alignItems: 'center',
    '& > .chevron-icon': {
        width: '15px',
        height: '10px',
        ...(isActive && {
            transform: 'rotate(180deg)'
        })
    }
}));

export const TenantName = styled('span')(() => ({
    marginRight: '8px',
    textTransform: 'uppercase'
}));

const DropdownButton = styled('button')<{ isCurrentTenant: boolean }>(({ isCurrentTenant = false, theme }) => ({
    fontFamily: 'TT Commons Pro Condensed',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '20px',
    letterSpacing: '0.02em',
    color: isCurrentTenant ? theme.palette.semantic.int.error : theme.palette.semantic.text.brand,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    marginRight: 'auto',
    textTransform: 'uppercase',
    padding: 0,
    '&:not(:last-child)': {
        marginBottom: '8px'
    },
    '&:hover': {
        color: theme.palette.semantic.int.error
    }
}));

const DropdownLegend = styled('span')(({ theme }) => ({
    fontFamily: 'TT Commons Pro',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
    color: theme.palette.semantic.text.muted,
    marginBottom: '18px'
}));

type Props = {
    currentTenant: string;
    onToggleDropdown: () => void;
    isDropdownActive: boolean;
};

export const NavTenant = ({ currentTenant, onToggleDropdown, isDropdownActive }: Props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedTenant = useRef<string>('');
    const { t } = useTranslation();
    const tenantsDict = useAppSelector((state) => state?.auth?.user?.userStatus);
    const admin_tenantListFromApi = useAppSelector((state) => state?.auth?.user?.admin_tenantList);
    let userInfo = useAppSelector((state) => state?.auth?.user);
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();

    let formattedOptions: Array<{ value: string; label: string; disabled: boolean }> = admin_tenantListFromApi?.map(
        (tenant: { value: string; label: string }) => ({
            value: tenant.value,
            label: tenant.label,
            disabled: tenantsDict[tenant.value] ?? false
        })
    );

    const handleTenantClick = async (tenant: string) => {
        if (userInfo?.tenant === tenant) {
            onToggleDropdown();
            return;
        }
        await dispatch(updateUserInfo({ ...userInfo, tenant, clickWrapAgreements: undefined }));
        dispatch(
            getUserRelatedInfo({
                tenant: tenant,
                userRole: (userInfo?.zoneRoles[tenant] && userInfo?.zoneRoles[tenant]['role']) || 'advisor'
            })
        );
        const proposalPaginationSearchFFEnabled =
            checkFFEnabled(FEATURE_NAMES['PROPOSALS_PAGINATION_SEARCH_FE_AEX-2382']) || false;
        if (proposalPaginationSearchFFEnabled) {
            dispatch(advisorConsoleActions.setTenantRefresh(true));
            const isFlow2fortheSelectedTenant = getClientJourneyFlowType(tenant) === 'flow2';
            dispatch(
                advisorConsoleActions.resetForTenantSwitch({
                    isFlow2: isFlow2fortheSelectedTenant,
                    isMultiPortfolioTenant
                })
            );
        }

        if (proposalPaginationSearchFFEnabled) {
            const DEFAULT_ITEMS_PER_PAGE = 10;
            const DEFAULT_PAGE = 1;
            dispatch(
                getAdvisorConsoleProposals({
                    tenant,
                    page: DEFAULT_PAGE,
                    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
                    proposalPaginationSearchFFEnabled,
                    onload: true
                })
            );
        } else {
            dispatch(getAdvisorConsoleProposals({ tenant }));
        }

        navigate(ADVISOR_ROUTES.ADVISOR_CONSOLE, { replace: true });
        onToggleDropdown();
    };

    useEffect(() => {
        if (selectedTenant.current !== currentTenant) {
            let tenantObj = formattedOptions.find((tenant: { [key: string]: any }) => tenant.value === currentTenant);
            if (!tenantObj) {
                tenantObj = formattedOptions[0];
            }
            selectedTenant.current = tenantObj.label;
        }
    }, [currentTenant]);

    return (
        <>
            <NavLink onClick={onToggleDropdown} id="nav-tenant-switcher">
                <Container isActive={isDropdownActive}>
                    <TenantName>{selectedTenant.current}</TenantName>
                    <Icon id="chevron-down" className="chevron-icon" />
                </Container>
            </NavLink>
            <NavDropdown isVisible={isDropdownActive} parentId="nav-tenant-switcher" onClose={onToggleDropdown}>
                <DropdownLegend>{t('TEXT_SELECT_TENANT')}</DropdownLegend>
                {formattedOptions.map((tenant: { value: string; label: string; disabled: boolean }) => (
                    <DropdownButton
                        key={tenant.value}
                        type="button"
                        isCurrentTenant={tenant.value === userInfo?.tenant}
                        onClick={() => handleTenantClick(tenant.value)}
                    >
                        {tenant.label}
                    </DropdownButton>
                ))}
            </NavDropdown>
        </>
    );
};
